/**
 * AvailabilityEditor — 7-day grid where employees mark each day as available/unavailable
 * and pick start/end times.
 *
 * Props:
 *   value:   { days: { monday: {available, start_time, end_time, notes}, ... }, notes }
 *   onChange(next) — called with the same shape on every edit
 *   readOnly?: boolean — render disabled inputs
 */
import React from 'react';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const DAYS = [
  ['monday', 'Mon'],
  ['tuesday', 'Tue'],
  ['wednesday', 'Wed'],
  ['thursday', 'Thu'],
  ['friday', 'Fri'],
  ['saturday', 'Sat'],
  ['sunday', 'Sun'],
];

const defaultDay = () => ({ available: true, start_time: '09:00', end_time: '17:00', notes: '' });

export const AvailabilityEditor = ({ value, onChange, readOnly = false }) => {
  const days = value?.days || {};
  const notes = value?.notes || '';

  const updateDay = (key, patch) => {
    if (readOnly) return;
    onChange({
      ...value,
      days: {
        ...days,
        [key]: { ...(days[key] || defaultDay()), ...patch },
      },
    });
  };

  const updateNotes = (next) => {
    if (readOnly) return;
    onChange({ ...value, notes: next });
  };

  return (
    <div className="space-y-4" data-testid="availability-editor">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-gray-500">
              <th className="py-2 pr-4">Day</th>
              <th className="py-2 pr-4">Available</th>
              <th className="py-2 pr-4">From</th>
              <th className="py-2 pr-4">To</th>
              <th className="py-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {DAYS.map(([key, label]) => {
              const d = days[key] || defaultDay();
              return (
                <tr key={key} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium text-gray-700">{label}</td>
                  <td className="py-2 pr-4">
                    <Switch
                      checked={!!d.available}
                      onCheckedChange={(checked) => updateDay(key, { available: checked })}
                      disabled={readOnly}
                      data-testid={`avail-toggle-${key}`}
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <Input
                      type="time"
                      value={d.start_time || ''}
                      onChange={(e) => updateDay(key, { start_time: e.target.value })}
                      disabled={readOnly || !d.available}
                      className="w-28"
                      data-testid={`avail-start-${key}`}
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <Input
                      type="time"
                      value={d.end_time || ''}
                      onChange={(e) => updateDay(key, { end_time: e.target.value })}
                      disabled={readOnly || !d.available}
                      className="w-28"
                      data-testid={`avail-end-${key}`}
                    />
                  </td>
                  <td className="py-2">
                    <Input
                      value={d.notes || ''}
                      onChange={(e) => updateDay(key, { notes: e.target.value })}
                      disabled={readOnly}
                      placeholder="e.g., school pickup at 3"
                      className="w-full"
                      data-testid={`avail-note-${key}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <Label className="text-xs">General notes (optional)</Label>
        <Textarea
          value={notes}
          onChange={(e) => updateNotes(e.target.value)}
          disabled={readOnly}
          rows={2}
          placeholder="Anything else managers should know about your availability..."
          data-testid="availability-notes"
        />
      </div>
    </div>
  );
};

export default AvailabilityEditor;
